// src/components/Header.tsx
import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { useSitePhone } from "@/hooks/useSitePhone";
import { serviceAreaNav } from "@/data/serviceAreaNav";
const tronLogo = { url: "/logos/tron-gold-logo.webp" };

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isToronto = location.pathname.startsWith("/toronto");
  const { phone, phoneLink } = useSitePhone();

  // Close the mobile menu whenever the route changes so the new page is visible
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // While the mobile menu is open: lock background scroll and hide the sticky
  // bottom CTA bar (it used to sit on top of the lowest menu links, swallowing taps).
  useEffect(() => {
    document.body.classList.toggle("mobile-menu-open", isMobileMenuOpen);
    return () => document.body.classList.remove("mobile-menu-open");
  }, [isMobileMenuOpen]);



  // Service-area strip: only the main markets, then a quiet "more" link.
  const QUICK_AREAS = ["Toronto", "Etobicoke", "Markham", "Vaughan", "Oakville", "Mississauga"];
  const quickLinks = QUICK_AREAS.map((name) => {
    const found = serviceAreaNav.find((a) => a.name === name);
    return found || { name, url: `/areas-we-serve/${name.toLowerCase().replace(/ /g, "-")}` };
  });

  return (
    <header
      className="relative z-50 overflow-visible text-primary-foreground"
      style={{
        background: "linear-gradient(180deg, #121212 0%, #1A1A1A 55%, #151515 100%)",
        color: "#F5E6C4",
        boxShadow: "0 18px 40px -28px rgba(0,0,0,0.9)",
      }}
    >
      {/* Gold hairline at the very top */}
      <div
        aria-hidden
        className="h-[2px] w-full"
        style={{ background: "linear-gradient(90deg, transparent, #D4AF37 20%, #F5E6C4 50%, #D4AF37 80%, transparent)" }}
      />

      <div className="relative overflow-x-hidden" style={{ color: "#F5E6C4" }}>
        {/* ROW 2: Logo + wordmark */}
        <div className="relative flex items-center justify-center px-4 pt-3 pb-2 md:pt-5 md:pb-3">
          <a
            href="/"
            className="group flex items-center gap-3 md:gap-4"
            aria-label="Tron Electrical — home"
          >
            <img
              src={tronLogo.url}
              alt="Tron Electrical"
              className="h-[40px] md:h-[60px] w-auto object-contain shrink-0 transition-transform duration-500 group-hover:scale-[1.03]"
              loading="eager"
            />
            <span className="flex flex-col items-start leading-none">
              <span
                className="block whitespace-nowrap bg-clip-text text-transparent"
                style={{
                  fontFamily:
                    '"Poppins", "Montserrat", "Inter", "Segoe UI", system-ui, -apple-system, Arial, sans-serif',
                  fontSize: "clamp(15px, 3.9vw, 36px)",
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                backgroundImage:
                    "linear-gradient(100deg, #F5E6C4 0%, #FFFFFF 22%, #D4AF37 55%, #F5E6C4 100%)",
                  filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.65))",
                }}
              >
                TRON ELECTRICAL &amp; AUTOMATION
              </span>
              <span
                className="mt-1.5 hidden md:block text-[10.5px] uppercase tracking-[0.42em]"
                style={{ color: "rgba(245,230,196,0.55)" }}
              >
                From small jobs to major electrical projects
              </span>
            </span>
          </a>
        </div>

        {/* ROW 3: Service-area strip — narrow, quiet, main markets only */}
        <div
          className="relative border-t px-3 py-1"
          style={{
            borderColor: "rgba(212,175,55,0.15)",
            background: "linear-gradient(180deg, rgba(212,175,55,0.04), rgba(212,175,55,0.01))",
          }}
        >
          <nav
            aria-label="Service areas"
            className="flex flex-wrap items-center justify-center gap-x-0.5 gap-y-0"
            style={{
              fontFamily: '"Cormorant Garamond", Garamond, Georgia, "Times New Roman", serif',
            }}
          >
            {quickLinks.map((area, i) => (
              <span key={area.url} className="flex items-center">
                {i > 0 && (
                  <span aria-hidden className="mx-1 inline-block h-[2px] w-[2px] rotate-45 bg-[#D4AF37]" />
                )}
                <Link
                  to={area.url}
                  className="px-1 py-[1px] text-[11px] italic tracking-[0.05em] transition-colors duration-300"
                  style={{ color: "hsl(var(--accent))" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--accent-light))")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "hsl(var(--accent))")}
                >
                  {area.name}
                </Link>
              </span>
            ))}
            <span aria-hidden className="mx-1 inline-block h-[2px] w-[2px] rotate-45 bg-[#D4AF37]" />
            <Link
              to="/gta"
              className="px-1 py-[1px] text-[10px] italic tracking-[0.05em] transition-colors duration-300"
              style={{ color: "hsl(var(--accent)/0.72)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--accent-light))")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "hsl(var(--accent)/0.72)")}
            >
              more areas
            </Link>
          </nav>
        </div>
      </div>


      {/* Main navigation row */}
      <nav className="relative w-full flex items-center justify-between px-3 h-[64px]">
        <div className="flex items-center justify-between min-w-0 flex-1">
          <div className="hidden lg:flex min-w-0 items-center gap-2 xl:gap-3 z-10 flex-wrap flex-1 justify-center">
            <Navigation />
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.06] backdrop-blur-md border border-[#D4AF37]/30 text-[#F5E6C4] hover:bg-accent hover:text-primary hover:border-accent transition-all duration-300 min-h-[44px] touch-manipulation ml-auto"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
            <span className="text-sm font-semibold tracking-[0.12em] uppercase">Menu</span>
          </button>
        </div>
      </nav>

      {/* Gold hairline closing the masthead (desktop) */}
      <div
        aria-hidden
        className="hidden lg:block h-px w-full"
        style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.55) 25%, rgba(245,230,196,0.75) 50%, rgba(212,175,55,0.55) 75%, transparent)" }}
      />

      {/* Mobile phone CTA - eye-catching premium gold bar */}
      <div className="relative lg:hidden w-full border-t-2 border-accent bg-primary/95 backdrop-blur-md py-2.5">
        <div className="container mx-auto px-4 flex justify-center">
          <a
            href={phoneLink}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-accent bg-accent text-primary text-sm font-bold tracking-wide
                       no-underline transition-all duration-300 hover:bg-accent-light hover:border-accent-light shadow-[0_0_18px_rgba(212,175,55,0.45)]"
            aria-label={`Call Tron Electrical at ${phone}`}
          >
            <Phone className="h-4 w-4 flex-shrink-0" />
            <span>Call {phone}</span>
            <span className="mx-2 h-4 w-px bg-primary/30" aria-hidden="true" />
            <span className="opacity-90">Available 24/7</span>
          </a>
        </div>
      </div>

      {/* Mobile menu — inline dropdown under the header (not full-screen) */}
      {isMobileMenuOpen && (
        <div className="lg:hidden relative z-[60] w-full border-t border-accent/20 bg-primary text-primary-foreground shadow-2xl">
          <nav className="max-h-[70vh] overflow-y-auto overscroll-contain flex flex-col items-center space-y-3 px-6 py-4">
            <Navigation />
          </nav>
        </div>
      )}

    </header>
  );
};

export default Header;

